import { useEffect, useLayoutEffect, useRef } from 'react';

import { fingerprintSelector, usernameSelector } from 'store/selectors';
import { api, LoggerService, tracker } from 'services';
import { useIVSPlayer, useStore } from 'hooks';

import styles from './PastEvent.Playback.module.scss';

export type PastEventPlaybackProps = {
  eventCode: string;
  playbackUrl: string;
  disabled?: boolean;
};

const PastEventPlayback = ({ eventCode, playbackUrl, disabled = false }: PastEventPlaybackProps): JSX.Element => {
  const { username, fingerprint } = useStore(state => ({
    username: usernameSelector(state),
    fingerprint: fingerprintSelector(state)
  }));

  const counted = useRef(false);
  const { videoEl, playerState, pause } = useIVSPlayer({
    playbackUrl,
    active: true
  });
  const isPlaying = playerState === IVSPlayer.PlayerState.PLAYING;

  useEffect(() => {
    const preventVideoPlay = () => {
      if (!videoEl.current) return;
      pause();
    };

    if (disabled && isPlaying) {
      pause();
      videoEl.current?.addEventListener('play', preventVideoPlay);

      return () => {
        videoEl.current?.removeEventListener('play', preventVideoPlay);
      };
    }

    return () => {};
  }, [disabled]);

  useEffect(() => {
    if (!counted.current && isPlaying) {
      incrementWatchCount();
      tracker.playPastEventRecording(eventCode);
    }
  }, [eventCode, isPlaying]);

  useLayoutEffect(() => {
    const handleOnEnterPiP = () => {
      tracker.enablePiPOnPastEvent();
    };

    videoEl.current?.addEventListener('enterpictureinpicture', handleOnEnterPiP);

    return () => {
      videoEl.current?.removeEventListener('enterpictureinpicture', handleOnEnterPiP);
    };
  }, []);

  const incrementWatchCount = async () => {
    try {
      await api.requests.ltv.incrementWatchCount(eventCode, username || fingerprint);
      counted.current = true;
    } catch (error) {
      LoggerService.log('INCREMENT_WATCH_COUNT', error);
    }
  };

  return (
    <div className={styles.container}>
      <video className={styles.video} ref={videoEl} muted playsInline controls />
    </div>
  );
};

export default PastEventPlayback;
